import { useState, useEffect, useRef } from 'react';
import { eventTryoutAPI, exportAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Plus, Pencil, Trash2, Upload, Users, Download, Search, 
  CheckCircle, XCircle, Loader2, FileSpreadsheet 
} from 'lucide-react';
import { toast } from 'sonner';

export default function EventParticipantsDialog({ open, event, onClose }) {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [uploading, setUploading] = useState(false);
  const [addDialog, setAddDialog] = useState({ open: false, mode: 'add', data: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, name: '' });
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open && event?.id) {
      loadParticipants();
    }
  }, [open, event?.id]);

  const loadParticipants = async () => {
    try {
      setLoading(true);
      const res = await eventTryoutAPI.getParticipants(event.id);
      setParticipants(res.data.participants || []);
    } catch (err) {
      console.error('Failed to load participants:', err);
      toast.error('Gagal memuat data peserta');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.match(/\.(xlsx|xls|csv)$/i)) {
      toast.error('File harus berformat Excel (.xlsx, .xls) atau CSV');
      return;
    }

    setUploading(true);
    try {
      const res = await eventTryoutAPI.uploadParticipantsExcel(event.id, file);
      toast.success(res.data.message);
      loadParticipants();
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.response?.data?.detail || 'Gagal upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAddParticipant = async (data) => {
    try {
      if (addDialog.mode === 'add') {
        await eventTryoutAPI.addParticipant(event.id, {
          event_id: event.id,
          ...data
        });
        toast.success('Peserta berhasil ditambahkan');
      } else {
        await eventTryoutAPI.updateParticipant(event.id, addDialog.data.id, data);
        toast.success('Data peserta berhasil diperbarui');
      }
      setAddDialog({ open: false, mode: 'add', data: null });
      loadParticipants();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal menyimpan data');
    }
  };

  const handleDelete = async () => {
    try {
      await eventTryoutAPI.deleteParticipant(event.id, deleteDialog.id);
      toast.success('Peserta berhasil dihapus');
      setDeleteDialog({ open: false, id: null, name: '' });
      loadParticipants();
    } catch (err) {
      toast.error('Gagal menghapus peserta');
    }
  };

  const filteredParticipants = participants.filter(p =>
    p.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.email?.toLowerCase().includes(search.toLowerCase()) ||
    p.phone?.includes(search) ||
    p.school?.toLowerCase().includes(search.toLowerCase()) ||
    p.participant_number?.toLowerCase().includes(search.toLowerCase())
  );

  const handleExportExcel = () => {
    if (!event?.id) return;
    
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Sesi login tidak valid');
      return;
    }
    
    const url = exportAPI.participantsExcelUrl(event.id);
    
    fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    .then(res => {
      if (!res.ok) throw new Error('Export gagal');
      return res.blob();
    })
    .then(blob => {
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `peserta_${event.title?.replace(/\s+/g, '_') || 'event'}.xlsx`;
      link.click();
      URL.revokeObjectURL(downloadUrl);
      toast.success('Berhasil export peserta ke Excel');
    })
    .catch(err => {
      console.error('Excel export error:', err);
      toast.error('Gagal export ke Excel');
    });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Users className="w-5 h-5 text-[#4472C4]" />
              Peserta Event: {event?.title}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Actions Bar */}
            <div className="flex items-center gap-2 mb-3">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Cari nama, email, HP, sekolah..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 h-8 text-sm"
                />
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleUploadExcel}
                className="hidden"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportExcel}
                className="h-8 text-xs border-green-600 text-green-600 hover:bg-green-50"
                disabled={participants.length === 0}
              >
                <FileSpreadsheet className="w-3 h-3 mr-1" />
                Export
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="h-8 text-xs"
              >
                {uploading ? (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <Upload className="w-3 h-3 mr-1" />
                )}
                Upload Excel
              </Button>
              <Button
                size="sm"
                onClick={() => setAddDialog({ open: true, mode: 'add', data: null })}
                className="h-8 text-xs bg-[#4472C4] hover:bg-[#3861a8]"
              >
                <Plus className="w-3 h-3 mr-1" />
                Tambah Manual
              </Button>
            </div>

            {/* Info */}
            <div className="flex items-center gap-4 mb-3 text-xs text-slate-500">
              <span>Total: <b className="text-slate-900">{participants.length}</b> peserta</span>
              <span className="text-[10px]">
                Format Excel: Nama, Email, No HP, Asal Sekolah/Instansi
              </span>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto border rounded-lg">
              {loading ? (
                <div className="flex items-center justify-center h-40">
                  <Loader2 className="w-6 h-6 text-[#4472C4] animate-spin" />
                </div>
              ) : filteredParticipants.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-slate-500">
                  <FileSpreadsheet className="w-10 h-10 mb-2 opacity-50" />
                  <p className="text-sm">
                    {search ? 'Tidak ada hasil ditemukan' : 'Belum ada peserta'}
                  </p>
                  {!search && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="w-3 h-3 mr-1" />
                      Upload Excel
                    </Button>
                  )}
                </div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-[#4472C4] text-white sticky top-0">
                    <tr>
                      <th className="px-2 py-2 text-left">No. Peserta</th>
                      <th className="px-2 py-2 text-left">Nama</th>
                      <th className="px-2 py-2 text-left">Email</th>
                      <th className="px-2 py-2 text-left">No HP</th>
                      <th className="px-2 py-2 text-left">Asal Sekolah/Instansi</th>
                      <th className="px-2 py-2 text-center">Status</th>
                      <th className="px-2 py-2 text-center">Aksi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {filteredParticipants.map((p) => (
                      <tr key={p.id} className="hover:bg-slate-50">
                        <td className="px-2 py-1.5">
                          <Badge variant="outline" className="font-mono text-[10px]">
                            {p.participant_number}
                          </Badge>
                        </td>
                        <td className="px-2 py-1.5 font-medium">{p.name}</td>
                        <td className="px-2 py-1.5 text-slate-600">{p.email}</td>
                        <td className="px-2 py-1.5 text-slate-600">{p.phone || '-'}</td>
                        <td className="px-2 py-1.5 text-slate-600 max-w-[150px] truncate" title={p.school}>
                          {p.school || '-'}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          {p.has_attempted ? (
                            <Badge className="bg-green-100 text-green-700 text-[10px]">
                              <CheckCircle className="w-2.5 h-2.5 mr-0.5" />
                              Sudah ({p.attempt_count}x)
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] text-slate-500">
                              Belum
                            </Badge>
                          )}
                        </td>
                        <td className="px-2 py-1.5">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-blue-600"
                              onClick={() => setAddDialog({ open: true, mode: 'edit', data: p })}
                            >
                              <Pencil className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-red-600"
                              onClick={() => setDeleteDialog({ open: true, id: p.id, name: p.name })}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <DialogFooter className="pt-3">
            <Button variant="outline" onClick={onClose}>Tutup</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Participant Dialog */}
      <ParticipantFormDialog
        open={addDialog.open}
        mode={addDialog.mode}
        data={addDialog.data}
        onClose={() => setAddDialog({ open: false, mode: 'add', data: null })}
        onSave={handleAddParticipant}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(o) => !o && setDeleteDialog({ open: false, id: null, name: '' })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Peserta?</AlertDialogTitle>
            <AlertDialogDescription>
              Anda akan menghapus peserta <b>{deleteDialog.name}</b> dari event ini.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function ParticipantFormDialog({ open, mode, data, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    school: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data) {
      setFormData({
        name: data.name || '',
        email: data.email || '',
        phone: data.phone || '',
        school: data.school || ''
      });
    } else {
      setFormData({ name: '', email: '', phone: '', school: '' });
    }
  }, [data, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error('Nama dan Email wajib diisi');
      return;
    }
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-sm">
            {mode === 'add' ? 'Tambah Peserta Manual' : 'Edit Data Peserta'}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Nama Lengkap *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Nama peserta"
              className="h-8 text-sm"
              required
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Email *</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="email@example.com"
              className="h-8 text-sm"
              required
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs">No HP</Label>
            <Input
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="081234567890"
              className="h-8 text-sm"
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Asal Sekolah/Instansi</Label>
            <Input
              value={formData.school}
              onChange={(e) => setFormData({ ...formData, school: e.target.value })}
              placeholder="SMA Negeri 1 Jakarta"
              className="h-8 text-sm"
            />
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>Batal</Button>
            <Button type="submit" size="sm" disabled={saving} className="bg-[#4472C4] hover:bg-[#3861a8]">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : mode === 'add' ? 'Tambah' : 'Simpan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
